import { MyFavoritesFragment } from '../../../generated/graphql';
import React, { ComponentProps, FC, useState } from 'react';
import styled from 'styled-components';
import FavoriteTile from '../../FavoriteTile';
import createImgProps from '../../../utils/createImgProps';
import { Box, Flex } from '@rebass/grid';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { FormattedMessage } from 'react-intl';
import Checkbox from '@oberoninternal/travelbase-ds/components/form/Checkbox';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import Stack from '../../Stack';
import EmptyState from './EmptyState';
import Anchor from '../../designsystem/Anchor';
import Link from 'next/link';
import SearchBottomBar from '../../SearchBottomBar';
import { Label } from '@oberoninternal/travelbase-ds/components/primitive/Label';

interface Props {
    data: MyFavoritesFragment[];
}

export const myFavoritesFragmentToProps = (favorite: MyFavoritesFragment): ComponentProps<typeof FavoriteTile> => {
    switch (favorite.__typename) {
        case 'FavoriteActivity':
            return {
                type: 'activity',
                title: favorite.activity?.name ?? '',
                id: favorite.activity?.id ?? '',
                favoriteId: favorite.id,
                imgProps:
                    favorite.activity?.jpegThumbnail &&
                    favorite.activity?.webpThumbnail &&
                    createImgProps(favorite.activity?.jpegThumbnail, favorite.activity?.webpThumbnail),
                slug: favorite.activity?.slug ?? '',
            };
        case 'FavoriteCompany':
            return {
                type: 'company',
                title: favorite.company?.name ?? '',
                id: favorite.company?.id ?? '',
                favoriteId: favorite.id,
                imgProps:
                    favorite.company?.jpegThumbnail &&
                    favorite.company?.webpThumbnail &&
                    createImgProps(favorite.company?.jpegThumbnail, favorite.company?.webpThumbnail),
                slug: favorite.company?.slug ?? '',
            };
        case 'FavoriteRentalUnit':
            return {
                type: 'rentalUnit',
                title: favorite.rentalUnit?.name ?? '',
                id: favorite.rentalUnit?.id ?? '',
                favoriteId: favorite.id,
                imgProps:
                    favorite.rentalUnit?.jpegThumbnail &&
                    favorite.rentalUnit?.webpThumbnail &&
                    createImgProps(favorite.rentalUnit?.jpegThumbnail, favorite.rentalUnit?.webpThumbnail),
                slug: favorite.rentalUnit?.slug ?? '',
            };
        default:
            throw new Error(`Unexpected favorite type: ${favorite.__typename}`);
    }
};

const Favorites: FC<React.PropsWithChildren<Props>> = ({ data }) => {
    const amount = data?.length ?? 0;
    const [showAccoFavorites, setShowAccoFavorites] = useState(true);
    const [showCompanyFavorites, setShowCompanyFavorites] = useState(true);
    const [showActivityFavorites, setShowActivityFavorites] = useState(true);
    const mobileFilters = useSesame(false);
    const filters = (
        <FavoriteFilters
            showAccoFavorites={showAccoFavorites}
            setShowAccoFavorites={setShowAccoFavorites}
            showCompanyFavorites={showCompanyFavorites}
            setShowCompanyFavorites={setShowCompanyFavorites}
            showActivityFavorites={showActivityFavorites}
            setShowActivityFavorites={setShowActivityFavorites}
        />
    );

    return (
        <>
            <Flex py={[6, null, '64px']} flexDirection={['column', null, null, 'row']}>
                {data.length > 0 && (
                    <Sidebar>
                        <Box mb={[5, null, '24px']}>
                            <Body variant="huge">
                                <span style={{ fontWeight: 'bold' }}>{amount}</span>{' '}
                                <FormattedMessage
                                    values={{
                                        amount,
                                    }}
                                    defaultMessage="{amount, plural, one {favoriet} other {favorieten}}"
                                />
                            </Body>
                        </Box>
                        <Box className="gt-l">{filters}</Box>
                    </Sidebar>
                )}
                <Wrapper flex={1}>
                    {data.length === 0 && (
                        <EmptyState
                            title={
                                <FormattedMessage defaultMessage="Je hebt momenteel nog geen opgeslagen favorieten." />
                            }
                            description={
                                <FormattedMessage
                                    defaultMessage="Sla <activityLink>activiteiten</activityLink>, <accoLink>accommodaties</accoLink> of <businessLink>bedrijven</businessLink> gemakkelijk op via het hartje."
                                    values={{
                                        activityLink: children => (
                                            <Link href="/agenda" legacyBehavior>
                                                <Anchor>{children}</Anchor>
                                            </Link>
                                        ),
                                        accoLink: children => (
                                            <Link href="/search" legacyBehavior>
                                                <Anchor>{children}</Anchor>
                                            </Link>
                                        ),
                                        businessLink: children => (
                                            <Link href="/companies" legacyBehavior>
                                                <Anchor>{children}</Anchor>
                                            </Link>
                                        ),
                                    }}
                                />
                            }
                        />
                    )}

                    {data
                        .filter(favorite => (!showAccoFavorites ? favorite.__typename !== 'FavoriteRentalUnit' : true))
                        .filter(favorite => (!showCompanyFavorites ? favorite.__typename !== 'FavoriteCompany' : true))
                        .filter(favorite =>
                            !showActivityFavorites ? favorite.__typename !== 'FavoriteActivity' : true
                        )
                        .map(favorite => (
                            <FavoriteTile
                                key={favorite.id}
                                {...myFavoritesFragmentToProps(favorite)}
                                showFavoriteButton
                            />
                        ))}
                </Wrapper>
            </Flex>

            <SearchBottomBar
                allowedActions={['filters']}
                filtersActive={mobileFilters.open}
                onFiltersClick={mobileFilters.onToggle}
            />
            <Modal {...mobileFilters}>{filters}</Modal>
        </>
    );
};

const FavoriteFilters: FC<
    React.PropsWithChildren<{
        showAccoFavorites: boolean;
        setShowAccoFavorites: (func: (v: boolean) => boolean) => void;
        showCompanyFavorites: boolean;
        setShowCompanyFavorites: (func: (v: boolean) => boolean) => void;
        showActivityFavorites: boolean;
        setShowActivityFavorites: (func: (v: boolean) => boolean) => void;
    }>
> = ({
    showAccoFavorites,
    setShowAccoFavorites,
    showCompanyFavorites,
    setShowCompanyFavorites,
    showActivityFavorites,
    setShowActivityFavorites,
}) => (
    <Stack spacing={4}>
        <Label>
            <FormattedMessage defaultMessage="Type" />
        </Label>
        <Checkbox id="showAccos" checked={showAccoFavorites} onChange={() => setShowAccoFavorites(v => !v)}>
            <FormattedMessage defaultMessage="Accommodatie" />
        </Checkbox>
        <Checkbox
            id="showActivities"
            checked={showActivityFavorites}
            onChange={() => setShowActivityFavorites(v => !v)}
        >
            <FormattedMessage defaultMessage="Activiteit" />
        </Checkbox>
        <Checkbox id="showCompanies" checked={showCompanyFavorites} onChange={() => setShowCompanyFavorites(v => !v)}>
            <FormattedMessage defaultMessage="Bedrijf" />
        </Checkbox>
    </Stack>
);

const Wrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    row-gap: 3.2rem;
`;

const Sidebar = styled(Box)`
    max-width: 32.8rem;
    flex: 1;
`;

export default Favorites;
